import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import FrameLogo from '.././Icons/frame-logo.png';
import AudioLogo from '.././Icons/audio-logo.svg';
import CamsLogo from '.././Icons/cams-logo.svg';
import TransferLogo from '.././Icons/transfer-logo.svg';

import AvailableFrameRate from './AvailableOptions/AvailableFrameRate';
import AvailableTracks from './AvailableOptions/AvailableTracks';
import AvailableTier from './AvailableOptions/AvailableTier';
import AvailableModes from './AvailableOptions/AvailableModes';
import AvailableStorageUnits from './AvailableOptions/AvailableStorageUnits';

import StorageSize from './UI/StorageSize';
import RecordDuration from './UI/RecordDuration';
import MyLabel from './UI/MyLabel';
import StyledTextfield from './UI/StyledTextfield';
import StyledButton from './UI/StyledButton';
import StyledSelect from './UI/StyledSelect';
import DeleteButton from './UI/DeleteButton';
import TierMenu from './UI/TierMenu';
import ShareButtons from './UI/ShareButtons';

import Button from '@mui/material/Button';
import PaperStyled from '../PaperStyled';

import StorageCalculation from '../Helper/StorageCalculation';
import BitRateCalculation from '../Helper/BitRateCalculation';
import RecordDurationCalculations from '../Helper/RecordDurationsCalculations';
import { GetStringToShareInformation } from '../Helper/GetStringToShareInformation';

import { getAvailableCams } from './AvailableOptions/AvailableCams';
import TransferBandwidth from './UI/TransferBandwidth';

import {
  getFrameRateFromLocalStorage,
  saveFrameRateToLocalStorage,
  getAudioTracksFromLocalStorage,
  saveAudioTracksToLocalStorage,
  getCamsFromLocalStorage,
  saveCamsToLocalStorage,
  getTierListFromLocalStorage,
  saveTierListToLocalStorage,
  getDaysFromLocalStorage,
  saveDaysToLocalStorage,
  getHoursFromLocalStorage,
  saveHoursToLocalStorage,
  getMinutesFromLocalStorage,
  saveMinutesToLocalStorage,
  getModeFromLocalStorage,
  saveModeToLocalStorage,
  saveStorageUnitsToLocalStorage,
  saveStorageResultToLocalStorage,
  getRecordModeFromLocalStorage,
  saveRecordModeToLocalStorage,
  getAvailableModesOptionsFromLocalStorage,
  saveAvailableModesOptionsToLocalStorage
} from '../Utils/LocalStorageHelper';

const StorageOrRecordCalculations = ({ isMobile, storageResult, setStorageResult, storageUnits, setStorageUnits }) => {
  const LoadInitialAvailableTier = () => {
    let initialFrameRate = getFrameRateFromLocalStorage(AvailableFrameRate[0]);
    let value = AvailableTier.rates.find((item) => item.rate === initialFrameRate.label);
    if (value) {
      return value.values;
    }
    return AvailableTier.rates[0].values;
  };

  const [frameRate, setFrameRate] = useState(getFrameRateFromLocalStorage(AvailableFrameRate[0]));
  const [selectedAvailableTier, setSelectedAvailableTier] = useState(LoadInitialAvailableTier());
  const [audioTracks, setAudioTracks] = useState(getAudioTracksFromLocalStorage(AvailableTracks[1]));
  const availableCams = getAvailableCams(1, 100);
  const [camsQty, setCamsQty] = useState(getCamsFromLocalStorage(availableCams[0]));
  const [days, setDays] = useState(getDaysFromLocalStorage(0));
  const [hours, setHours] = useState(getHoursFromLocalStorage(1));
  const [minutes, setMinutes] = useState(getMinutesFromLocalStorage(0));
  const [mode, setMode] = useState(getRecordModeFromLocalStorage(AvailableModes[1]));
  const [availableModesOptions, setAvailableModesOptions] = useState(getAvailableModesOptionsFromLocalStorage(AvailableModes));
  const [tierList, setTierList] = useState(getTierListFromLocalStorage([{ resolution: '', label: '', bit_rate: '' }]));
  const [isStorageCalculatorActive, setStorageCalculatorIsActive] = useState(getModeFromLocalStorage(true));
  const [bitRateTotal, setBitRateTotal] = useState(0);

  const updateFrameRateValue = (value) => {
    setFrameRate(value);
    saveFrameRateToLocalStorage(value);
  };

  const updateAudioTracksValue = (value) => {
    setAudioTracks(value);
    saveAudioTracksToLocalStorage(value);
  };

  const updateCamsValue = (value) => {
    setCamsQty(value);
    saveCamsToLocalStorage(value);
  };

  const updateTierListValue = (value) => {
    setTierList(value);
    saveTierListToLocalStorage(value);
  };

  const updateDaysValue = (value) => {
    setDays(value);
    saveDaysToLocalStorage(value);
  };

  const updateHoursValue = (value) => {
    setHours(value);
    saveHoursToLocalStorage(value);
  };

  const updateMinutesValue = (value) => {
    setMinutes(value);
    saveMinutesToLocalStorage(value);
  };

  const updateStorageUnitsValue = (value) => {
    setStorageUnits(value);
    saveStorageUnitsToLocalStorage(value);
  };

  const updateStorageResultValue = useCallback(
    (value) => {
      setStorageResult(value);
      saveStorageResultToLocalStorage(value);
    },
    [setStorageResult]
  );

  const updateModeValue = (value) => {
    setMode(value);
    saveRecordModeToLocalStorage(value);
  };

  const updateAvailableModesOptionsValue = (value) => {
    setAvailableModesOptions(value);
    saveAvailableModesOptionsToLocalStorage(value);
  };

  const resetSettings = () => {
    updateFrameRateValue(AvailableFrameRate[0]);
    let newTierList = {
      resolution: '',
      label: '',
      bit_rate: ''
    };
    updateTierListValue([newTierList]);
    updateAudioTracksValue(AvailableTracks[1]);
    updateCamsValue(availableCams[0]);
    updateDaysValue(0);
    updateHoursValue(1);
    updateMinutesValue(0);
    updateStorageResultValue(0);
    updateStorageUnitsValue(AvailableStorageUnits[2]);
  };

  const addNewTierHandler = () => {
    updateTierListValue([
      ...tierList,
      {
        resolution: '',
        label: '',
        bit_rate: ''
      }
    ]);
  };

  const removeTierHandler = (index) => {
    let list = [...tierList];
    list.splice(index, 1);
    updateTierListValue(list);
  };

  const onChangeDirectionHandler = () => {
    setStorageCalculatorIsActive(!isStorageCalculatorActive);
    saveModeToLocalStorage(!isStorageCalculatorActive);
  };

  useEffect(() => {
    let value = AvailableTier.rates.find((item) => item.rate === frameRate.label);
    if (value) {
      setSelectedAvailableTier(value.values);
    }
  }, [frameRate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let bitRateTotal = BitRateCalculation(tierList, audioTracks, camsQty);
    setBitRateTotal(parseFloat(bitRateTotal).toFixed(2));
    if (isStorageCalculatorActive) {
      updateStorageResultValue(StorageCalculation({ days, hours, minutes, bitRateTotal, storageUnits }));
    } else {
      RecordDurationCalculations({
        bitRateTotal,
        storageResult,
        storageUnits,
        mode,
        setDays: updateDaysValue,
        setHours: updateHoursValue,
        setMinutes: updateMinutesValue,
        minutes
      });
    }
  }, [audioTracks, camsQty, tierList, days, hours, minutes, isStorageCalculatorActive, storageUnits, storageResult, mode, updateStorageResultValue]);

  let informationToShare = `${GetStringToShareInformation({ frameRate, audioTracks, camsQty, storageResult, storageUnits, days, hours, minutes, tierList })}`;

  let needToHaveDeleteOnTier = tierList.length > 1;

  return (
    <>
      <Grid container paddingLeft={3} paddingRight={3} spacing={2} paddingBottom={'20px'}>
        <Grid item xs={6}>
          <Typography component="div" variant="h6" color="white" mt="10px" textAlign="left" sx={{ fontWeight: 'bold' }}>
            {isStorageCalculatorActive ? 'Video Storage' : 'Video Record Duration'}
          </Typography>
        </Grid>
        <Grid item xs={6} style={{ textAlign: 'right', marginTop: '10px' }}>
          <ShareButtons subject={'Calculator Result'} informationToShare={informationToShare} />
        </Grid>
        <Grid item xs={4}>
          <PaperStyled>
            <MyLabel logo={FrameLogo} label="Frame Rate" />
            <StyledSelect
              id="frame-rate-select"
              labelId="frame-rate-select-label"
              value={frameRate}
              setValue={(value) => {
                updateFrameRateValue(value);
                let newTierList = {
                  resolution: '',
                  label: '',
                  bit_rate: ''
                };
                updateTierListValue([newTierList]);
              }}
              optionsList={AvailableFrameRate}
            />
          </PaperStyled>
        </Grid>
        <Grid item xs={4}>
          <PaperStyled>
            <MyLabel logo={AudioLogo} label="Audio Tracks" />
            <StyledSelect
              id="audio-tracks-select"
              labelId="audio-tracks-select-label"
              value={audioTracks}
              setValue={updateAudioTracksValue}
              optionsList={AvailableTracks}
            />
          </PaperStyled>
        </Grid>
        <Grid item xs={4}>
          <PaperStyled>
            <MyLabel logo={CamsLogo} label="Cams" />
            {/* <StyledTextfield id="cams-number" type="int" value={camsQty} minValue={1} setValue={setCamsQty} /> */}
            <StyledSelect id="cams-select" labelId="cams-select-label" value={camsQty} setValue={updateCamsValue} optionsList={availableCams} />
          </PaperStyled>
        </Grid>
        <Grid item md={9} xs={8}></Grid>
        <Grid item md={3} xs={4}>
          <PaperStyled>
            <MyLabel logo="" label="Bit Rate [Mbps]" marginBottom="-8px" />
          </PaperStyled>
        </Grid>
        {tierList.map((tierItem, index) => {
          return (
            <React.Fragment key={'TierItem' + index}>
              <Grid item md={1.5} xs={2}>
                <PaperStyled>
                  <MyLabel logo="" label={`Tier ${index + 1}`} />
                </PaperStyled>
              </Grid>
              <Grid item md={7.5} xs={6}>
                <PaperStyled>
                  <TierMenu
                    id="tier-select"
                    labelId="tier-select-label"
                    label="Tier"
                    optionsList={selectedAvailableTier}
                    value={tierItem}
                    setValue={(value) => {
                      let newTierList = [...tierList];
                      newTierList[index] = value;
                      updateTierListValue(newTierList);
                    }}
                  />
                </PaperStyled>
              </Grid>
              <Grid item md={needToHaveDeleteOnTier ? 2.5 : 3} xs={needToHaveDeleteOnTier ? 3 : 4} paddingLeft={'0px'}>
                <PaperStyled>
                  <StyledTextfield
                    id={'bit-rate' + index}
                    type="float"
                    value={tierItem.bit_rate}
                    minValue={0}
                    setValue={(value) => {
                      let newTierList = [...tierList];
                      newTierList[index].bit_rate = value;
                      newTierList[index].label = 'custom';
                      newTierList[index].resolution = 'custom';
                      updateTierListValue(newTierList);
                    }}
                  />
                </PaperStyled>
              </Grid>
              {needToHaveDeleteOnTier && (
                <Grid
                  item
                  xs={1}
                  md={0.5}

                  // marginBottom="8px" marginLeft="-8px"
                >
                  <PaperStyled style={{ height: '100%', display: 'flex', alignItems: 'center', marginLeft: '-8px' }}>
                    <DeleteButton
                      onClickHandler={() => {
                        removeTierHandler(index);
                      }}
                    />
                  </PaperStyled>
                </Grid>
              )}

              {index === tierList.length - 1 && (
                <Grid item md={12} xs={12}>
                  <PaperStyled>
                    <StyledButton onClickHandler={addNewTierHandler} label="Add Tier" height="30px" width="100%" />
                  </PaperStyled>
                </Grid>
              )}
            </React.Fragment>
          );
        })}
        {isStorageCalculatorActive ? (
          <RecordDuration
            id={'record'}
            days={days}
            setDays={updateDaysValue}
            hours={hours}
            setHours={updateHoursValue}
            minutes={minutes}
            setMinutes={updateMinutesValue}
            mode={mode}
            setMode={updateModeValue}
            availableModesOptions={availableModesOptions}
            setAvailableModesOptions={updateAvailableModesOptionsValue}
            isStorageCalculatorActive={isStorageCalculatorActive}
          />
        ) : (
          <StorageSize
            id={'storage-size'}
            storageResult={storageResult}
            setStorageResult={updateStorageResultValue}
            isDisabled={isStorageCalculatorActive ? true : false}
            storageUnits={storageUnits}
            setStorageUnits={updateStorageUnitsValue}
            isDisabledUnits={false}
          />
        )}
        <Grid item md={2} xs={12} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '33px' }}>
          <Button variant="contained" sx={{ padding: '0px', cursor: 'default' }}>
            <img
              src={TransferLogo}
              alt="label"
              style={{ height: '40px', cursor: 'pointer', transform: isMobile ? 'rotate(90deg)' : '' }}
              onClick={onChangeDirectionHandler}
            />
          </Button>
        </Grid>
        {isStorageCalculatorActive ? (
          <StorageSize
            storageResult={storageResult}
            setStorageResult={updateStorageResultValue}
            isDisabled={isStorageCalculatorActive ? true : false}
            storageUnits={storageUnits}
            setStorageUnits={updateStorageUnitsValue}
            isDisabledUnits={false}
          />
        ) : (
          <RecordDuration
            days={days}
            setDays={updateDaysValue}
            hours={hours}
            setHours={updateHoursValue}
            minutes={minutes}
            setMinutes={updateMinutesValue}
            mode={mode}
            setMode={updateModeValue}
            availableModesOptions={availableModesOptions}
            setAvailableModesOptions={updateAvailableModesOptionsValue}
            isStorageCalculatorActive={isStorageCalculatorActive}
          />
        )}

        <Grid item md={12} xs={12}>
          <TransferBandwidth bitRateTotal={bitRateTotal} />
        </Grid>

        <Grid item md={12} xs={12}>
          <PaperStyled>
            <StyledButton
              onClickHandler={resetSettings}
              label={isStorageCalculatorActive ? 'Clear Video Storage Calculator' : 'Clear Video Record Duration Calculator'}
              height="30px"
              width="100%"
            />
          </PaperStyled>
        </Grid>
      </Grid>
    </>
  );
};

export default StorageOrRecordCalculations;
